import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Megaphone, 
  Lock, 
  User, 
  Mail, 
  Calendar,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getUserInitials, getProfilePictureUrl } from '@/utils/profileUtils';
import ChangePasswordModal from '@/components/ChangePasswordModal';

interface AdvertisingProfileProps {
  profileUser?: any;
}

const AdvertisingProfile = ({ profileUser }: AdvertisingProfileProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);

  const displayUser = profileUser || user;
  const isOwnProfile = !profileUser || profileUser?.id === user?.id;

  if (!displayUser) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Profile Not Found</h1>
          <p className="text-gray-600">Unable to load profile information.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Profile Header */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <Avatar className="h-24 w-24">
              <AvatarImage src={getProfilePictureUrl(displayUser)} />
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                {getUserInitials(displayUser?.name || displayUser?.email)}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
                <h1 className="text-2xl font-bold">{displayUser.name || displayUser.email}</h1>
                <Badge variant="secondary" className="w-fit mx-auto md:mx-0">
                  <Megaphone className="h-3 w-3 mr-1" />
                  Advertising
                </Badge>
              </div>
              <p className="text-muted-foreground">{displayUser.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      {isOwnProfile && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              onClick={() => navigate('/advertising')} 
              className="w-full justify-between"
              size="lg"
            >
              <div className="flex items-center gap-2">
                <Megaphone className="h-5 w-5" />
                Go to Advertising Dashboard
              </div>
              <ArrowRight className="h-5 w-5" />
            </Button>
            
            <Button 
              onClick={() => setIsChangePasswordModalOpen(true)} 
              variant="outline"
              className="w-full justify-between"
              size="lg"
            >
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Change Password
              </div>
              <ArrowRight className="h-5 w-5" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Account Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{displayUser.email}</p>
            </div>
          </div>
          
          <Separator />
          
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Role</p>
              <p className="font-medium capitalize">{displayUser.role || 'Advertising'}</p>
            </div>
          </div>
          
          <Separator />
          
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Account Created</p>
              <p className="font-medium">
                {displayUser.created 
                  ? new Date(displayUser.created).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  : 'N/A'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Modal */}
      <ChangePasswordModal 
        isOpen={isChangePasswordModalOpen} 
        onClose={() => setIsChangePasswordModalOpen(false)} 
      />
    </div>
  );
};

export default AdvertisingProfile;
